import {
  ButtonComponentProps,
  DropdownList,
  DropdownListItem,
  DropdownWithButton,
  Icon,
  LabelRegular
} from '@hm/fundamental'
import {mdiMagnify} from '@mdi/js'
import React, {forwardRef, useEffect, useRef, useState} from 'react'

import {SelectButton} from './SelectButton'

export type SelectGroupedOption = {
  label: string
  subLabel?: string
  value?: string
  disabled?: boolean
}

type SelectGroupedProps = {
  inputLabel: string
  options: SelectGroupedOption[]
  value: string
  onChange: (value: string) => void
  caption?: string
  searchPlaceholder?: string
  disabled?: boolean
  'data-test-id'?: string
  showSearch?: boolean
  error?: string
  emptyLabel?: string
  isReadOnly?: boolean
}

export const SelectGrouped = ({
  options,
  value,
  onChange,
  inputLabel,
  searchPlaceholder,
  disabled,
  showSearch,
  'data-test-id': dataTestId,
  error,
  emptyLabel,
  isReadOnly
}: SelectGroupedProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listWrapperRef = useRef<HTMLDivElement>(null)

  const filteredOptions = options.filter(
    (option) =>
      option.value &&
      (!searchTerm.trim() || option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchTerm])

  useEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return

    const element = dropdownRef.current.querySelector(
      `[data-cy="${dataTestId}-item-${filteredOptions[highlightedIndex]?.value}"]`
    )

    if (element) {
      element.scrollIntoView({block: 'nearest', behavior: 'smooth'})
    }
  }, [highlightedIndex, filteredOptions, dataTestId])

  const selectOption = () => {
    if (highlightedIndex < 0 || highlightedIndex >= filteredOptions.length) return

    const selectedOption = filteredOptions[highlightedIndex]
    if (selectedOption?.value) {
      onChange(selectedOption.value)
      setSearchTerm('')
      document.body.click()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
    if (filteredOptions.length === 0 && e.key !== 'Escape') return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        e.stopPropagation()
        setHighlightedIndex((prev) =>
          prev === -1 ? 0 : prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        e.stopPropagation()
        setHighlightedIndex((prev) =>
          prev === -1
            ? filteredOptions.length - 1
            : prev > 0
              ? prev - 1
              : filteredOptions.length - 1
        )
        break
      case 'Tab':
      case 'Enter':
        if (highlightedIndex >= 0) {
          e.preventDefault()
          selectOption()
        }
        break
      case 'Escape':
        e.preventDefault()
        setSearchTerm('')
        document.body.click()
        break
    }
  }

  const selectedLabel = options.find((option) => option.value === value)?.label
  return (
    <div
      ref={dropdownRef}
      className={`w-full relative ${
        isReadOnly && 'pointer-events-none'
      } max-w-[600px] outline-none`}
    >
      <DropdownWithButton
        variant="secondary"
        label={selectedLabel}
        dropdownTop={1}
        scrollable={true}
        className="w-full"
        component={forwardRef<any, ButtonComponentProps>(
          (props: {
            label?: string
            onClick?: React.MouseEventHandler<HTMLElement>
            isDropdownOpen: boolean
          }) => (
            <SelectButton
              {...props}
              inputLabel={inputLabel}
              emptyLabel={emptyLabel}
              isReadOnly={isReadOnly}
              error={error}
              disabled={disabled}
              dataTestId={dataTestId}
              showSearch={showSearch}
              searchInputRef={searchInputRef}
              listWrapperRef={listWrapperRef}
            />
          )
        )}
      >
        {showSearch && (
          <div className="relative">
            <Icon
              path={mdiMagnify}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              data-test-id={`search-input-${dataTestId}`}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-2 py-2 border-1px border-border rounded bg-container_background outline-none h-[50px]"
            />
          </div>
        )}
        <div
          ref={listWrapperRef}
          tabIndex={showSearch ? -1 : 0}
          onKeyDown={handleKeyDown}
          className="outline-none"
        >
          <DropdownList
            onSelectItem={(item) => {
              onChange(item.value?.toString() || '')
              setSearchTerm('')
              document.body.click()
            }}
          >
            {options.map((option) => {
              const isInFilteredList = filteredOptions.some(
                (filtered) => filtered.value === option.value
              )

              if (searchTerm.trim() && !isInFilteredList) return null

              if (!option.value) {
                return (
                  <DropdownListItem
                    key={option.label}
                    title={option.label}
                    className="-ml-2 opacity-30 pointer-events-none"
                  />
                )
              }

              const filteredIndex = filteredOptions.findIndex(
                (filtered) => filtered.value === option.value
              )
              const isHighlighted = filteredIndex === highlightedIndex

              return (
                <DropdownListItem
                  key={option.value}
                  value={option.value}
                  title={option.label}
                  subtitle={option.subLabel}
                  data-cy={`${dataTestId}-item-${option.value}`}
                  className={isHighlighted ? '!bg-container_background_elevated_hover' : ''}
                />
              )
            })}
          </DropdownList>
        </div>
      </DropdownWithButton>
      {error && (
        <LabelRegular className="block mt-3px px-12px bg-container text-negative">
          {error}
        </LabelRegular>
      )}
    </div>
  )
}
